import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';
// window.TomSelect = TomSelect

export default class extends Controller {
  static values = {
    options: Array,
    disableSearch: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    removableOption: { type: Boolean, default: true },
    optionRender: { type: String, default: '' },
    allowEmptyOption: { type: Boolean, default: false },
  };

  connect() {
    if (!this.element.tomselect) {
      (() => new TomSelect(this.element, this._settingsFactory(this.element)))();
    }

    this.#forwardAccessibleName();
  }

  // TomSelect hides the original <select> and builds its own combobox, which
  // inherits none of its ARIA attributes. Without this, the control exposed to
  // the user is a role="combobox" with no accessible name.
  #forwardAccessibleName() {
    const tomSelect = this.element.tomselect;
    if (!tomSelect) { return; }

    const control = tomSelect.control_input?.isConnected
      ? tomSelect.control_input
      : tomSelect.control;
    if (!control) { return; }

    const labelledBy = this.element.getAttribute('aria-labelledby');
    if (labelledBy) {
      control.setAttribute('aria-labelledby', labelledBy);
      return;
    }

    const ariaLabel = this.element.getAttribute('aria-label');
    if (ariaLabel) { control.setAttribute('aria-label', ariaLabel); }
  }

  disconnect() {
    const value = this.element.value;

    this.element.tomselect.destroy();

    // necessary to persist value because
    // this.element.tomselect.destroy() sets the value to null
    this.element.value = value;
  }

  _settingsFactory(element) {
    const ajaxUrl = element.dataset.ajaxUrl;
    const ajaxTextField = element.dataset.ajaxTextField;
    const ajaxValueField = element.dataset.ajaxValueField;
    const ajaxMatcher = element.dataset.ajaxSearchMatcher;
    const ajaxDisplayedField = (
      element.dataset.ajaxDisplayedField ? element.dataset.ajaxDisplayedField : ajaxTextField
    );
    const ajaxAdditionalFilters = (
      element.dataset.ajaxAdditionalFilters
        ? JSON.parse(element.dataset.ajaxAdditionalFilters)
        : []
    );

    const additionalFiltersQuery = ajaxAdditionalFilters.reduce((acc, filter) => {
      return acc + `&q[${filter[0]}]=${filter[1]}`;
    }, '');

    const formatAjaxData = (json, valueField, textField) => {
      return json.map(entry => (
        {
          value: entry[valueField],
          text: entry[textField]
        }
      ));
    };

    const settingsStore = {
      create: {
        create: input => ({ value: '', text: input }),
        persist: false
      },
      ajax: {
        valueField: 'value',
        labelField: 'text',
        searchField: 'text',
        load: function (query, callback) {
          const searchQuery = `q[${ajaxTextField}_${ajaxMatcher}]=${encodeURIComponent(query)}`;
          const url = `${ajaxUrl}?${searchQuery}${additionalFiltersQuery}`;
          fetch(url, {
            headers: {
              Accept: 'application/json'
            }
          })
            .then(response => response.json())
            .then(json => {
              callback(formatAjaxData(json, ajaxValueField, ajaxDisplayedField));
            }).catch((e) => {
              callback();
            });
        }
      }
    };

    const placeholder = this.placeholderValue || '';
    const allowEmptyOption = this.allowEmptyOptionValue;

    const settings = {
      placeholder: placeholder,
      allowEmptyOption: allowEmptyOption,
      maxOptions: null,
      onItemAdd: function () {
        this.setTextboxValue('');
      },
      plugins: this.removableOptionValue ? { remove_button: { title: 'remove' } } : {},
    };

    if (this.disableSearchValue) {
      settings.controlInput = null;
      delete settings.plugins.remove_button;
    }

    if (this.hasOptionsValue) { Object.assign(settings, settings, { options: this.optionsValue }); }
    if (element.classList.contains('tom-select-ajax')) { Object.assign(settings, settings, settingsStore.ajax); }
    if (element.classList.contains('tom-select-create')) { Object.assign(settings, settings, settingsStore.create); }

    const optionRender = this._optionRenderSettings();
    if (optionRender) { Object.assign(settings, settings, optionRender); }

    return settings;
  }

  _optionRenderSettings() {
    switch (this.optionRenderValue) {
      case 'profile_balance':
        return this._profileBalanceRender();
      case 'country_flag':
        return this._countryFlagRender();
      default:
        return null;
    }
  }

  _countryFlagRender() {
    const template = (data, escape) => {
      if (!data.value) { return `<div>${escape(data.text)}</div>`; }

      return '<div class="d-flex align-items-center gap-2">' +
        `<span class="fi fi-${escape(data.value.toLowerCase())}" aria-hidden="true"></span>` +
        `<span>${escape(data.text)}</span></div>`;
    };

    return {
      render: {
        option: template,
        item: template,
      },
    };
  }

  _profileBalanceRender() {
    const template = (data, escape) => {
      const balance = data.balance
        ? `<div class="text-gray-600 small">${escape(data.balance)}</div>`
        : '';

      return '<div class="py-1"><div class="d-flex flex-column">' +
        `<div>${escape(data.text)}</div>${balance}</div></div>`;
    };

    return {
      render: {
        option: template,
        item: template,
      },
    };
  }
}
